'use client';

import React from 'react';

import { TextIframeBlockProps } from '@/app/_blocks/TextIframeBlockGraphQL/types';
import { Button } from '../Button';
import { Gutter } from '../Gutter';

import classes from './TextIframeBlock.module.scss';
import { renderTrademarkText } from '../TrademarkText';

type ButtonType = NonNullable<TextIframeBlockProps['buttons']>[number]['button'];
interface TextIframeButton extends ButtonType {
  textIframeButtonStyle: ButtonType['style'];
  textIframeButtonType: ButtonType['type'];
}

export const TextIframeBlock: React.FC<{
  className?: string;
  textIframeBlock: TextIframeBlockProps;
}> = ({ className, textIframeBlock }) => {
  const {
    title,
    description,
    buttons = [],
    reversed,
    src,
    customDesktopAspectRatio,
    desktopAspectRatioWidth,
    desktopAspectRatioHeight,
    customMobileAspectRatio,
    mobileAspectRatioWidth,
    mobileAspectRatioHeight,
  } = textIframeBlock;

  // Calculate desktop aspect ratio
  const desktopAspectRatio =
    customDesktopAspectRatio && desktopAspectRatioWidth && desktopAspectRatioHeight
      ? `${desktopAspectRatioWidth}/${desktopAspectRatioHeight}`
      : '16/9';

  // Calculate mobile aspect ratio
  const mobileAspectRatio =
    customMobileAspectRatio && mobileAspectRatioWidth && mobileAspectRatioHeight
      ? `${mobileAspectRatioWidth}/${mobileAspectRatioHeight}`
      : '4/3';

  const reversedClass = reversed ? classes.reversed : '';

  return (
    <Gutter>
      <div
        className={[className, classes.wrapper, 'grid', reversedClass].filter(Boolean).join(' ')}
      >
        <div
          className={`col-8 col-sm-12 ${classes.iframeColumn}`}
          style={
            {
              '--desktop-aspect-ratio': desktopAspectRatio,
              '--mobile-aspect-ratio': mobileAspectRatio,
            } as React.CSSProperties
          }
        >
          <div className={classes.iframeWrapper}>
            <iframe
              src={src || ''}
              title={title?.text || 'Embedded content'}
              allowFullScreen
              style={{ height: '100%', width: '100%', position: 'absolute' }}
            />
          </div>
        </div>
        <div className={`col-4 col-sm-12 ${classes.textColumn}`}>
          {(() => {
            const headlineText = title?.text;
            const headlineTag = title?.tag || 'h2';
            const HeadlineTag = headlineTag as 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'span';
            return headlineText ? (
              <HeadlineTag className={`${classes.heading} heading-h2`}>{renderTrademarkText(headlineText)}</HeadlineTag>
            ) : null;
          })()}
          {description &&
            description
              .split(/\r?\n/)
              .filter(paragraph => paragraph.trim() !== '')
              .map((paragraph, idx) => (
                <p key={idx} className={classes.paragraph}>
                  {paragraph}
                </p>
              ))}
          {buttons && buttons.length > 0 && (
            <div className={classes.buttonRow}>
              {buttons.map(({ button }: { button: TextIframeButton }) =>
                button?.hideButton ? null : (
                  <Button
                    key={button?.label}
                    type={button?.textIframeButtonType || 'reference'}
                    appearance={button?.textIframeButtonStyle}
                    href={button?.textIframeButtonType === 'custom' ? button?.url : undefined}
                    reference={
                      button?.textIframeButtonType === 'reference' ? button?.reference : undefined
                    }
                    document={
                      button?.textIframeButtonType === 'download' &&
                      button?.document &&
                      typeof button.document === 'object'
                        ? button.document
                        : undefined
                    }
                    label={button?.label}
                    newTab={button?.newTab}
                  />
                ),
              )}
            </div>
          )}
        </div>
      </div>
    </Gutter>
  );
};

export default TextIframeBlock;
